// components/header/tools-panel.component.tsx
//
// The compact Tools dropdown that replaces the 109-link mega-menu.
// Layout: [search] → [popular chips] → [category tiles] → [see-all link]
//
// Search hits the same backend endpoint as <HeaderSearch /> (Lucene index in
// professorx). When the query is empty, the popular + categories view shows;
// while typing, results render inline below the input.

import React, { Component, createRef } from 'react';
import Router from 'next/router';
import { Subscription } from 'rxjs';

import { lazyInject } from '../../context/module';
import { ApiConfig } from '../../services/api-config';
import { LightHttp } from '../../services/light-http.service';
import { ToolsDropdownColumn } from 'xconvert-web-shared';
import { mediaToolsConfig } from '../../commons/media-tools.config';
import { HEADER_POPULAR_TOOLS } from '../../commons/site-nav';
import styles from './tools-panel.component.module.scss';

interface SearchResult {
    slug: string;
    url: string;
    title: string;
    category: string;
}

interface SearchResponse {
    results: SearchResult[];
    query: string;
    count: number;
}

// The "Popular" chips: xconvert-web-ssr sources/site-nav.json `header.popular` (commons/site-nav.ts; HANDOFF-L4 H5,
// 2026-10-11). Since link-hierarchy-20261026 the list is GENERATED there (top FP_TOP_FIXED FP-T1 pages by measured revenue,
// pipeline policy.json fp.header_popular) - never hand-edit, re-import.
const POPULAR_TOOLS = HEADER_POPULAR_TOOLS;

// Pull category tiles from the existing media-tools.config. Skip "all-tools"
// (footer link covers it) and "utility-tools" (no real hub page on QA).
// Keep "other-tools" — it's the 300+ developer/format/encode/validate hub,
// which is the destination users want when they look for JSON / regex / JWT
// / base64 / minify / hash etc. An earlier draft deep-linked tiles to single
// tools (Developer Tools → /json-formatter); replaced with the real hub.
const PRIMARY_CATEGORIES = mediaToolsConfig.filter((c) => !['all-tools', 'utility-tools'].includes(c.slug));

interface Props {
    /** Called when the panel should close (user clicked a link, hit Esc, etc.) */
    onClose: () => void;
    /**
     * The "Browse by task" groups: xconvert-web-ssr's `toolsDropdownData` (link-policy.ts `toolsDropdownFromPolicy`: 3 groups x
     * FP_DROPDOWN_GROUP_SIZE FP-T1/T2 pages, anchor = target H1). Optional - a build without the policy file renders chips + tiles only.
     */
    toolsDropdownData?: ToolsDropdownColumn[];
}

interface ToolsBrowseProps {
    toolsDropdownData?: ToolsDropdownColumn[];
    /** Called after a link click (the open panel closes itself); the server-rendered copy passes nothing. */
    onNavigate?: () => void;
}

/**
 * The panel's static browse view: Popular chips -> Browse by task (the policy's tools dropdown) -> Browse by category -> see all.
 * Stateless and SSR-safe on purpose: header-v2 renders it once more, server-side and `hidden`, while the panel is closed, so the
 * header's template links (link-hierarchy-20261026 METHOD §4 `header-popular` + `tools-dropdown`, D-GRAPH F4: they used to exist only
 * after a click) are in every page's HTML. The `data-block` markers are what the HTML assertion counts.
 */
export function ToolsBrowse({ toolsDropdownData, onNavigate }: ToolsBrowseProps) {
    const close = () => { if (onNavigate) onNavigate(); };
    const groups = (toolsDropdownData || []).flatMap((c) => c.groups || []).filter((g) => g && g.items && g.items.length > 0);
    return (
        <>
            <div className={styles.sectionLabel}>Popular</div>
            <div className={styles.popularRow} data-block="header-popular">
                {POPULAR_TOOLS.map((t) => (
                    <a key={t.href} href={t.href} className={styles.popularChip} onClick={close}>{t.text}</a>
                ))}
            </div>

            {groups.length > 0 && (
                <>
                    <div className={styles.sectionLabel}>Browse by task</div>
                    <div className={styles.taskGroups} data-block="tools-dropdown">
                        {groups.map((g) => (
                            <div key={g.name} className={styles.taskGroup}>
                                <div className={styles.taskGroupName}>{g.name}</div>
                                {g.items.map((i) => (
                                    <a key={i.href} href={i.href} className={styles.taskLink} onClick={close}>{i.name}</a>
                                ))}
                            </div>
                        ))}
                    </div>
                </>
            )}

            <div className={styles.sectionLabel}>Browse by category</div>
            <div className={styles.tileGrid}>
                {PRIMARY_CATEGORIES.map((c) => (
                    <a key={c.slug} href={`/${c.slug}`} className={styles.categoryTile} onClick={close}>
                        <span className={styles.tileIcon} aria-hidden="true">{c.icon}</span>
                        <span className={styles.tileName}>{c.name}</span>
                    </a>
                ))}
            </div>
        </>
    );
}

interface State {
    query: string;
    results: SearchResult[];
    isSearching: boolean;
    activeIndex: number;
}

export class ToolsPanel extends Component<Props, State> {
    @lazyInject(LightHttp) private lightHttp: LightHttp;
    @lazyInject(ApiConfig) private apiConfig: ApiConfig;

    private inputRef = createRef<HTMLInputElement>();
    private searchTimeout: ReturnType<typeof setTimeout>;
    private searchSubscription: Subscription;
    private navigating = false;

    state: State = {
        query: '',
        results: [],
        isSearching: false,
        activeIndex: -1,
    };

    componentDidMount() {
        // Auto-focus the search input on open — the most common path is "I know
        // what I want, just let me type" — making the field the default action.
        // Defer one frame so the slide-in animation doesn't fight focus.
        requestAnimationFrame(() => this.inputRef.current?.focus());
        document.addEventListener('keydown', this.onDocumentKeyDown);
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.onDocumentKeyDown);
        clearTimeout(this.searchTimeout);
        this.searchSubscription?.unsubscribe();
    }

    onDocumentKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') this.props.onClose();
    };

    onQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const query = e.target.value;
        this.setState({ query, activeIndex: -1 });

        clearTimeout(this.searchTimeout);
        this.searchSubscription?.unsubscribe();

        if (query.length < 2) {
            this.setState({ results: [], isSearching: false });
            return;
        }
        this.setState({ isSearching: true });

        this.searchTimeout = setTimeout(() => {
            this.searchSubscription = this.lightHttp.get<SearchResponse>(
                this.apiConfig.getFeatureSearchEndpoint(query),
            ).subscribe({
                next: (response) => this.setState({ results: response.results || [], isSearching: false }),
                error: () => this.setState({ isSearching: false }),
            });
        }, 250);
    };

    onInputKeyDown = (e: React.KeyboardEvent) => {
        const { results, activeIndex } = this.state;
        if (results.length === 0) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            this.setState({ activeIndex: Math.min(activeIndex + 1, results.length - 1) });
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            this.setState({ activeIndex: Math.max(activeIndex - 1, 0) });
        } else if (e.key === 'Enter' && activeIndex >= 0) {
            e.preventDefault();
            this.navigateTo(results[activeIndex].url);
        }
    };

    navigateTo = (url: string) => {
        if (this.navigating) return;
        this.navigating = true;
        Router.push(url).then(() => {
            this.navigating = false;
            this.props.onClose();
        });
    };

    private renderSearchInput() {
        const { query, isSearching } = this.state;
        return (
            <div className={styles.searchRow}>
                <svg className={styles.searchIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                    ref={this.inputRef}
                    className={styles.searchInput}
                    type="text"
                    placeholder="Search 5000+ tools…"
                    value={query}
                    onChange={this.onQueryChange}
                    onKeyDown={this.onInputKeyDown}
                    aria-label="Search tools"
                />
                {isSearching && <div className={styles.spinner} aria-hidden="true" />}
                {query && (
                    <button
                        type="button"
                        className={styles.clearBtn}
                        onClick={() => { this.setState({ query: '', results: [], activeIndex: -1 }); this.inputRef.current?.focus(); }}
                        aria-label="Clear search"
                    >×</button>
                )}
            </div>
        );
    }

    private renderResults() {
        const { results, isSearching, query, activeIndex } = this.state;
        if (results.length > 0) {
            return (
                <div className={styles.results} role="listbox">
                    {results.slice(0, 10).map((r, i) => (
                        <a
                            key={r.slug}
                            href={r.url}
                            className={`${styles.resultItem} ${i === activeIndex ? styles.resultActive : ''}`}
                            onClick={(e) => { e.preventDefault(); this.navigateTo(r.url); }}
                            onMouseEnter={() => this.setState({ activeIndex: i })}
                            role="option"
                            aria-selected={i === activeIndex}
                        >
                            <span className={styles.resultTitle}>{r.title}</span>
                            <span className={`${styles.resultBadge} ${styles[`cat${r.category}`] || ''}`}>{r.category}</span>
                        </a>
                    ))}
                    {results.length > 10 && (
                        <a className={styles.resultsMore} href={`/tools?q=${encodeURIComponent(query)}`} onClick={() => this.props.onClose()}>
                            See all {results.length} results →
                        </a>
                    )}
                </div>
            );
        }
        if (!isSearching && query.length >= 2) {
            return <div className={styles.empty}>No tools found for &ldquo;{query}&rdquo;.</div>;
        }
        return null;
    }

    private renderBrowse() {
        return <ToolsBrowse toolsDropdownData={this.props.toolsDropdownData} onNavigate={() => this.props.onClose()} />;
    }

    render() {
        const showResults = this.state.query.length >= 2;
        return (
            <div className={styles.panel} role="dialog" aria-label="Tools menu">
                {this.renderSearchInput()}
                {showResults ? this.renderResults() : this.renderBrowse()}
                <a className={styles.footerLink} href="/tools" onClick={() => this.props.onClose()}>
                    See all 5000+ tools →
                </a>
            </div>
        );
    }
}
